import React from 'react';
import styled from 'styled-components';
import {
  isBefore,
  startOfToday,
  setHours,
  getDayOfYear,
  setDayOfYear,
  getYear,
  setYear,
} from 'date-fns';
import { ColumnsType } from 'antd/es/table';
import { Alert } from '@brokerbay/design-components';
import { Timezone } from '@bb/domain/dist/types';
import {
  Icon,
  Select,
  DatePicker,
  TimePicker,
  Button,
  Table,
  Card,
  Text,
} from 'components';
import { useBreakpoint } from 'hooks';
import { getTimezoneCode } from 'utils';
import { CreateListingCardProps, OpenHousesFields } from '../types';
import { StyledGrid } from './StyledComponents';

type OpenHousesProps = CreateListingCardProps<OpenHousesFields> & {
  openHousesErrors: boolean[];
};

const OpenHouses: React.FC<OpenHousesProps> = ({
  timezone,
  openHouses,
  openHousesErrors,
  setListing,
}) => {
  const { md } = useBreakpoint();
  // Creating a listing w/ openhouses will use users local timezone
  const localTimezone = Intl.DateTimeFormat().resolvedOptions()
    .timeZone as Timezone;

  const addOpenHouse = () => {
    setListing(prev => ({
      ...prev,
      openHouses: [
        ...openHouses,
        {
          type: 'public',
          date: null,
          timeFrom: null,
          timeTo: null,
          isManualOpenHouse: true,
        },
      ],
    }));
  };

  const editOpenHouse = (
    changes: Partial<OpenHousesProps['openHouses'][number]>,
    i: number,
  ) => {
    const newOpenHouses = [...openHouses];
    newOpenHouses[i] = { ...newOpenHouses[i], ...changes };
    setListing(prev => ({
      ...prev,
      openHouses: newOpenHouses,
    }));
  };

  const deleteOpenHouse = (i: number) => {
    setListing(prev => ({
      ...prev,
      openHouses: openHouses.filter((o, idx) => idx !== i),
    }));
  };

  const columns: ColumnsType<OpenHousesProps['openHouses'][number]> = [
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 100,
      render: (value: 'agent' | 'public', _, i) => (
        <Select
          value={value}
          onChange={newValue => editOpenHouse({ type: newValue }, i)}
          options={[
            { value: 'public', label: 'Public' },
            { value: 'agent', label: 'Agent' },
          ]}
        />
      ),
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (value, record, i) => (
        <DatePicker
          allowClear
          value={value}
          format="MMM D, YYYY"
          error={openHousesErrors[i]}
          disabledDate={date => isBefore(date, startOfToday())}
          onChange={date => {
            if (!date) {
              editOpenHouse(
                { date, timeTo: undefined, timeFrom: undefined },
                i,
              );
              return;
            }
            const dayOfYear = getDayOfYear(date);
            const year = getYear(date);
            const defaultTimeFrom = setHours(startOfToday(), 14);
            const defaultTimeTo = setHours(startOfToday(), 16);

            const timeFrom = setYear(
              setDayOfYear(record?.timeFrom || defaultTimeFrom, dayOfYear),
              year,
            );
            const timeTo = setYear(
              setDayOfYear(record?.timeTo || defaultTimeTo, dayOfYear),
              year,
            );

            editOpenHouse({ date, timeTo, timeFrom }, i);
          }}
        />
      ),
    },
    {
      title: 'From',
      dataIndex: 'timeFrom',
      key: 'timeFrom',
      render: (value, record, i) => (
        <TimePicker
          inputReadOnly
          allowClear={false}
          use12Hours
          showNow={false}
          value={value}
          format="h:mma"
          error={openHousesErrors[i]}
          onChange={timeFrom => {
            editOpenHouse({ timeFrom }, i);
          }}
        />
      ),
    },
    {
      title: 'To',
      dataIndex: 'timeTo',
      key: 'timeTo',
      render: (value, record, i) => (
        <TimePicker
          inputReadOnly
          allowClear={false}
          use12Hours
          showNow={false}
          value={value}
          format="h:mma"
          error={openHousesErrors[i]}
          onChange={timeTo => editOpenHouse({ timeTo }, i)}
        />
      ),
    },
    {
      title: '',
      dataIndex: '',
      key: 'delete',
      fixed: 'right',
      width: 80,
      render: (value, record, i) => (
        <Button size="small" onClick={() => deleteOpenHouse(i)}>
          Delete
        </Button>
      ),
    },
  ];

  // Adjust openHouses.date: Date is in GMT and it will be adjusted to the user's timezone
  // when the listing is displayed, but we need to offset that adjustment here so that the
  // date is displayed correctly in the table.
  const timezoneOffset = new Date().getTimezoneOffset();
  const adjustedOpenHouses = openHouses.map(openHouse => ({
      ...openHouse,
      ...(openHouse.date && {
        date: new Date(openHouse.date.getTime() + timezoneOffset * 60000),
      }),
    }));

  return (
    <Card
      type="primary"
      title="Open Houses"
      subtitle={
        openHousesErrors.some(Boolean) && (
          <StyledErrorAlert
            type="error"
            showIcon
            message="End time must be after the start time."
          />
        )
      }
      id="openHouses"
      extra={
        <Button
          type="default"
          icon={<Icon type="plus" height={16} offset={[1, 0]} />}
          onClick={addOpenHouse}
        >
          {md && 'Add Open House'}
        </Button>
      }
    >
      {openHouses.length > 0 && (
        <Text>
          Times reflect the {timezone ? 'listing' : 'user'}&apos;s timezone (
          {getTimezoneCode(timezone || localTimezone)})
        </Text>
      )}

      <StyledGrid $columns={1}>
        <StyledTableWrapper>
          <Table
            size="small"
            dataSource={adjustedOpenHouses.map((openHouse, i) => ({
              ...openHouse,
              key: i,
            }))}
            columns={columns}
            pagination={false}
            scroll={{
              x: '600px',
            }}
          />
        </StyledTableWrapper>
      </StyledGrid>
    </Card>
  );
};

const StyledErrorAlert = styled(Alert)`
  margin-top: 25px;
  margin-right: 50px;
`;

const StyledTableWrapper = styled.div`
  grid-column-end: span 2;
`;

export default React.memo(OpenHouses);
